import { GlobalOutlined } from '@ant-design/icons';
import { Menu, type MenuProps } from 'antd';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useUiStore } from '@/app/ui-store';
import { useAuth } from '@/lib/auth/use-auth';
import { usePermission } from '@/lib/rbac/use-permission';
import { navSections } from './nav-config';

/** Brand-green sidebar rendered with Ant Menu groups; the active item is the longest matching route. */
export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const mobileOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileSidebar = useUiStore((s) => s.setMobileSidebar);
  const { role } = useAuth();
  const allowed = usePermission(role);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sections = useMemo(() => navSections
    .map((section) => ({ ...section, items: section.items.filter((i) => !i.requires || allowed(i.requires.action, i.requires.resource)) }))
    .filter((section) => section.items.length > 0), [allowed]);
  const items: MenuProps['items'] = sections.map((section) => ({
    type: 'group' as const,
    key: section.label,
    label: collapsed ? null : section.label,
    // Keys are namespaced by section because two sections may link to the same route (e.g. Favorites and Operations → /incidents).
    children: section.items.map((item) => ({ key: `${section.label}|${item.to}`, icon: <span className="nav-icon">{item.icon}</span>, label: item.label })),
  }));
  const selected = useMemo(() => {
    const all = sections.flatMap((s) => s.items.map((i) => ({ ...i, key: `${s.label}|${i.to}` })));
    const exact = all.filter((i) => i.end && i.to === pathname);
    if (exact.length) return exact.map((i) => i.key);
    const matches = all.filter((i) => !i.end && (pathname === i.to || pathname.startsWith(`${i.to}/`)));
    const longest = Math.max(0, ...matches.map((i) => i.to.length));
    return matches.filter((i) => i.to.length === longest).map((i) => i.key);
  }, [sections, pathname]);
  return (
    <>
      <aside id="sidebar" className={clsx(collapsed && 'collapsed', mobileOpen && 'mobile-open')} role="navigation" aria-label="Main navigation">
        <div className="sidebar-header"><span className="sidebar-header-text">Navigation</span></div>
        <Menu
          className="sidebar-nav"
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={selected}
          items={items}
          onClick={({ key }) => { navigate(String(key).split('|')[1] ?? '/'); setMobileSidebar(false); }}
          style={{ borderInlineEnd: 0, background: 'transparent' }}
        />
        <div className="sidebar-footer">
          <div className="nav-item" style={{ fontSize: 11 }}>
            <span className="nav-icon"><GlobalOutlined /></span>
            <span className="nav-label" style={{ color: 'var(--sn-sidebar-icon)' }}>v2.0.0 — VetsRelief ITSM</span>
          </div>
        </div>
      </aside>
      <div id="sidebar-backdrop" className={clsx(mobileOpen && 'visible')} onClick={() => setMobileSidebar(false)} />
    </>
  );
}
